import { useState, useEffect } from "react";
import { ArrowLeft, Tag, Video, Headphones, Link as LinkIcon, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { supabase } from "@/integrations/supabase/client";
import { slugify, isYouTubeUrl, isVimeoUrl, isSpotifyUrl, isSoundCloudUrl } from "@/lib/media-utils";

interface Post {
  id: string;
  title: string;
  slug: string | null;
  excerpt: string | null;
  content: string | null;
  image_url: string | null;
  video_url: string | null;
  audio_url: string | null;
  category: string | null;
  published_at: string | null;
}

interface PostEditorProps {
  post: Post | null;
  onSave: (postData: {
    title: string;
    slug: string;
    excerpt: string | null;
    content: string | null;
    image_url: string | null;
    video_url: string | null;
    audio_url: string | null;
    category: string | null;
    published_at: string | null;
  }) => Promise<void>;
  onCancel: () => void;
}

const CATEGORIES = [
  "Adventure",
  "Medical Tips",
  "Travel Mishaps",
  "Family Stories",
  "Destinations",
  "Gear & Packing",
];

const PostEditor = ({ post, onSave, onCancel }: PostEditorProps) => {
  const [title, setTitle] = useState(post?.title || "");
  const [slug, setSlug] = useState(post?.slug || "");
  const [excerpt, setExcerpt] = useState(post?.excerpt || "");
  const [content, setContent] = useState(post?.content || "");
  const [imageUrl, setImageUrl] = useState(post?.image_url || "");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [mediaLink, setMediaLink] = useState(post?.video_url || post?.audio_url || "");
  const [category, setCategory] = useState(post?.category || "");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [detectedType, setDetectedType] = useState<"video" | "audio" | null>(null);

  // Auto-generate slug from title
  useEffect(() => {
    if (!post) {
      setSlug(slugify(title));
    }
  }, [title, post]);

  // Auto-detect media type
  useEffect(() => {
    if (!mediaLink.trim()) {
      setDetectedType(null);
      return;
    }
    if (isYouTubeUrl(mediaLink) || isVimeoUrl(mediaLink)) {
      setDetectedType("video");
    } else if (isSpotifyUrl(mediaLink) || isSoundCloudUrl(mediaLink)) {
      setDetectedType("audio");
    } else {
      setDetectedType(null);
    }
  }, [mediaLink]);

  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return imageUrl || null;
    
    setIsUploading(true);
    try {
      const ext = imageFile.name.split(".").pop();
      const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      
      const { error } = await supabase.storage
        .from("blog-images")
        .upload(path, imageFile);
      
      if (error) throw error;
      
      const { data: { publicUrl } } = supabase.storage
        .from("blog-images")
        .getPublicUrl(path);
      
      return publicUrl;
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent, publish = false) => {
    e.preventDefault();
    if (!title.trim()) return;

    setIsSaving(true);
    try {
      const finalImageUrl = await uploadImage();
      
      await onSave({
        title: title.trim(),
        slug: slug.trim() || slugify(title.trim()),
        excerpt: excerpt.trim() || null,
        content: content.trim() || null,
        image_url: finalImageUrl,
        video_url: detectedType === "video" ? mediaLink.trim() : null,
        audio_url: detectedType === "audio" ? mediaLink.trim() : null,
        category: category || null,
        published_at: publish ? new Date().toISOString() : (post?.published_at || null),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="ghost" onClick={onCancel}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <h2 className="font-typewriter text-2xl font-bold text-primary">
          {post ? "Edit Post" : "New Post"}
        </h2>
      </div>

      <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-6">
        {/* Title */}
        <div className="space-y-2">
          <label htmlFor="title" className="text-sm font-medium text-foreground">Title *</label>
          <Input id="title" placeholder="Enter post title..." value={title} onChange={(e) => setTitle(e.target.value)} className="font-typewriter text-lg" required />
        </div>

        {/* Slug */}
        <div className="space-y-2">
          <label htmlFor="slug" className="text-sm font-medium text-foreground flex items-center gap-2">
            <LinkIcon className="w-4 h-4" /> URL Slug
          </label>
          <Input id="slug" placeholder="auto-generated-from-title" value={slug} onChange={(e) => setSlug(e.target.value)} className="font-mono text-sm" />
          <p className="text-xs text-muted-foreground">/field-notes/{slug || "..."}</p>
        </div>

        {/* Excerpt */}
        <div className="space-y-2">
          <label htmlFor="excerpt" className="text-sm font-medium text-foreground">Excerpt / Summary</label>
          <Textarea id="excerpt" placeholder="A short summary for cards and SEO..." value={excerpt} onChange={(e) => setExcerpt(e.target.value)} className="min-h-[80px]" />
        </div>

        {/* Category */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground flex items-center gap-2">
            <Tag className="w-4 h-4" /> Category
          </label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((cat) => (
              <button key={cat} type="button" onClick={() => setCategory(category === cat ? "" : cat)}
                className={`px-3 py-1 rounded-full text-sm font-typewriter transition-colors ${category === cat ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Image Upload */}
        <ImageUpload
          currentUrl={imageUrl}
          onImageSelected={(file) => setImageFile(file)}
          onClear={() => { setImageUrl(""); setImageFile(null); }}
        />

        {/* Media Link */}
        <div className="space-y-2">
          <label htmlFor="mediaLink" className="text-sm font-medium text-foreground flex items-center gap-2">
            {detectedType === "video" ? <Video className="w-4 h-4 text-accent" /> : detectedType === "audio" ? <Headphones className="w-4 h-4 text-secondary" /> : <LinkIcon className="w-4 h-4" />}
            Media Link (YouTube, Vimeo, Spotify, SoundCloud)
          </label>
          <Input id="mediaLink" type="url" placeholder="https://youtube.com/watch?v=... or https://open.spotify.com/..." value={mediaLink} onChange={(e) => setMediaLink(e.target.value)} />
          {detectedType && (
            <p className="text-xs font-typewriter">
              {detectedType === "video" ? "🎬 Detected: Video Post" : "🎧 Detected: Audio Post"}
            </p>
          )}
        </div>

        {/* Content */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Content</label>
          <RichTextEditor value={content} onChange={setContent} />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4 pt-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" variant="outline" disabled={isSaving || isUploading || !title.trim()}>
            {isUploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Uploading...</> : isSaving ? "Saving..." : "Save as Draft"}
          </Button>
          <Button type="button" onClick={(e) => handleSubmit(e, true)} disabled={isSaving || isUploading || !title.trim()} className="bg-accent hover:bg-accent/90">
            {isUploading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Uploading...</> : isSaving ? "Publishing..." : (post?.published_at ? "Update & Publish" : "Publish Now")}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PostEditor;
